import {
  EVIDENCE_SECTION_KEYS,
  M1_TITLE,
  M2_EVIDENCE_SECTION_KEYS,
  M2_TITLE,
  M3_EVIDENCE_SECTION_KEYS,
  M3_TITLE,
  MILESTONE_ID,
  MILESTONE_KEY,
  MILESTONE_STATUS,
} from "./constants";

/** @typedef {"confidential"|"ip"} SensitivityLevel */
/** @typedef {"pending"|"in_progress"|"completed"} MilestoneStatus */

/**
 * @typedef {Object} FormatFlags
 * @property {boolean} pdf
 * @property {boolean} image
 * @property {boolean} text
 */

/**
 * @typedef {Object} MilestoneAttachment
 * @property {string} assetRef
 * @property {string} [fileName]
 * @property {string} [mimeType]
 */

/**
 * @typedef {Object} EvidenceBlock
 * @property {FormatFlags} formatFlags
 * @property {SensitivityLevel} sensitivity
 * @property {MilestoneAttachment[]} attachments
 * @property {string} [textNotes]
 */

/**
 * @typedef {Object} GearUnitFinding
 * @property {string} id
 * @property {string} gearUnitSerialNumber
 * @property {EvidenceBlock} dimensionalMeasurements
 * @property {EvidenceBlock} ndtReports
 * @property {EvidenceBlock} visualInspectionImages
 */

/**
 * @typedef {Object} ServiceComponent
 * @property {string} id
 * @property {string} serialNumber
 */

/**
 * @typedef {Object} M1Inputs
 * @property {ServiceComponent[]} components
 * @property {EvidenceBlock} dimensionalMeasurements
 * @property {EvidenceBlock} ndtReports
 * @property {EvidenceBlock} visualInspectionImages
 * @property {GearUnitFinding[]} [findings]
 */

/**
 * @typedef {Object} M2Inputs
 * @property {EvidenceBlock} postRepairMeasurements
 * @property {EvidenceBlock} heatTreatmentReferences
 * @property {EvidenceBlock} processConfirmation
 */

/**
 * @typedef {Object} M3Inputs
 * @property {EvidenceBlock} finalInspectionReport
 * @property {boolean} serviceCompletionConfirmed
 */

/**
 * @typedef {Object} CustomEvidenceSection
 * @property {string} id
 * @property {string} label
 * @property {boolean} labelConfirmed
 * @property {EvidenceBlock} evidence
 */

/**
 * @typedef {Object} FollowUpMilestoneInputs
 * @property {boolean} serviceCompletionConfirmed
 * @property {CustomEvidenceSection[]} customSections
 */

/**
 * @typedef {Object} ServiceMilestone
 * @property {string} id
 * @property {string} key
 * @property {string} title
 * @property {MilestoneStatus} status
 * @property {number} sortOrder
 * @property {M1Inputs|M2Inputs|M3Inputs} [inputs]
 */

export const REMANUFACTURING_DEFAULT_MILESTONES = [
  {
    id: MILESTONE_ID.M1,
    key: MILESTONE_KEY.M1,
    title: M1_TITLE,
    status: MILESTONE_STATUS.PENDING,
    sortOrder: 1,
    inputs: createEmptyM1Inputs(),
  },
  {
    id: MILESTONE_ID.M2,
    key: MILESTONE_KEY.M2,
    title: M2_TITLE,
    status: MILESTONE_STATUS.PENDING,
    sortOrder: 2,
    inputs: createEmptyM2Inputs(),
  },
  {
    id: MILESTONE_ID.M3,
    key: MILESTONE_KEY.M3,
    title: M3_TITLE,
    status: MILESTONE_STATUS.PENDING,
    sortOrder: 3,
    inputs: createEmptyM3Inputs(),
  },
];

/**
 * @returns {string}
 */
export function createFindingId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `finding-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

/**
 * @returns {FormatFlags}
 */
export function createEmptyFormatFlags() {
  return { pdf: false, image: false, text: false };
}

/**
 * @returns {EvidenceBlock}
 */
export function createEmptyEvidenceBlock() {
  return {
    formatFlags: createEmptyFormatFlags(),
    sensitivity: "confidential",
    attachments: [],
    textNotes: "",
  };
}

/**
 * @param {string} [gearUnitSerialNumber]
 * @returns {GearUnitFinding}
 */
export function createEmptyFinding(gearUnitSerialNumber = "") {
  return {
    id: createFindingId(),
    gearUnitSerialNumber,
    dimensionalMeasurements: createEmptyEvidenceBlock(),
    ndtReports: createEmptyEvidenceBlock(),
    visualInspectionImages: createEmptyEvidenceBlock(),
  };
}

/**
 * @param {string} [serialNumber]
 * @returns {ServiceComponent}
 */
export function createEmptyServiceComponent(serialNumber = "") {
  return {
    id: createFindingId(),
    serialNumber,
  };
}

/**
 * @returns {M1Inputs}
 */
export function createEmptyM1Inputs() {
  return {
    components: [],
    dimensionalMeasurements: createEmptyEvidenceBlock(),
    ndtReports: createEmptyEvidenceBlock(),
    visualInspectionImages: createEmptyEvidenceBlock(),
  };
}

/**
 * @returns {M2Inputs}
 */
export function createEmptyM2Inputs() {
  return {
    postRepairMeasurements: createEmptyEvidenceBlock(),
    heatTreatmentReferences: createEmptyEvidenceBlock(),
    processConfirmation: createEmptyEvidenceBlock(),
  };
}

/**
 * @returns {M3Inputs}
 */
export function createEmptyM3Inputs() {
  return {
    finalInspectionReport: createEmptyEvidenceBlock(),
    serviceCompletionConfirmed: false,
  };
}

/**
 * @returns {CustomEvidenceSection}
 */
export function createEmptyCustomEvidenceSection() {
  return {
    id: createFindingId(),
    label: "",
    labelConfirmed: false,
    evidence: createEmptyEvidenceBlock(),
  };
}

/**
 * @param {unknown} raw
 * @returns {CustomEvidenceSection}
 */
export function normalizeCustomEvidenceSection(raw) {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return createEmptyCustomEvidenceSection();
  }
  const record = /** @type {Record<string, unknown>} */ (raw);
  const label = String(record.label ?? "").trim();
  const explicitConfirmed =
    record.labelConfirmed ?? record.label_confirmed;
  const labelConfirmed =
    explicitConfirmed != null ? Boolean(explicitConfirmed) : label.length > 0;
  return {
    id: String(record.id ?? createFindingId()),
    label,
    labelConfirmed: label.length > 0 ? labelConfirmed : false,
    evidence: normalizeEvidenceBlock(record.evidence),
  };
}

/**
 * @param {unknown} raw
 * @returns {CustomEvidenceSection[]}
 */
export function normalizeCustomSections(raw) {
  const sectionsRaw =
    raw && typeof raw === "object" && !Array.isArray(raw)
      ? /** @type {Record<string, unknown>} */ (raw).customSections ??
        /** @type {Record<string, unknown>} */ (raw).custom_sections
      : raw;
  if (!Array.isArray(sectionsRaw)) {
    return [];
  }
  return sectionsRaw.map(normalizeCustomEvidenceSection);
}

/**
 * @returns {FollowUpMilestoneInputs}
 */
export function createEmptyFollowUpInputs() {
  return {
    serviceCompletionConfirmed: false,
    customSections: [],
  };
}

/**
 * @param {CustomEvidenceSection[]} sections
 * @returns {boolean}
 */
export function hasCompleteCustomEvidenceSection(sections) {
  return sections.some(
    (section) =>
      Boolean(section.labelConfirmed) &&
      String(section.label ?? "").trim() &&
      evidenceBlockHasContent(section.evidence)
  );
}

/**
 * @param {string|null|undefined} key
 * @returns {number|null}
 */
export function milestoneNumberFromKey(key) {
  const match = /^M(\d+)$/.exec(String(key ?? ""));
  return match ? Number.parseInt(match[1], 10) : null;
}

/**
 * @param {string|null|undefined} key
 * @returns {boolean}
 */
export function isFollowUpMilestoneKey(key) {
  const number = milestoneNumberFromKey(key);
  return number != null && number >= 4;
}

/**
 * @param {string} key
 * @returns {string}
 */
export function nextFollowUpMilestoneKey(key) {
  const number = milestoneNumberFromKey(key);
  if (number == null || number < 3) {
    return MILESTONE_KEY.M4;
  }
  return `M${number + 1}`;
}

/**
 * @param {unknown} raw
 * @returns {FollowUpMilestoneInputs}
 */
export function normalizeFollowUpInputs(raw) {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return createEmptyFollowUpInputs();
  }
  const record = /** @type {Record<string, unknown>} */ (raw);
  return {
    serviceCompletionConfirmed: Boolean(
      record.serviceCompletionConfirmed ?? record.service_completion_confirmed
    ),
    customSections: normalizeCustomSections(record),
  };
}

/**
 * @param {ServiceMilestone|undefined|null} milestone
 * @returns {boolean}
 */
export function getServiceCompletionConfirmed(milestone) {
  if (milestone?.key === MILESTONE_KEY.M3) {
    return normalizeM3Inputs(milestone.inputs).serviceCompletionConfirmed;
  }
  if (isFollowUpMilestoneKey(milestone?.key)) {
    return normalizeFollowUpInputs(milestone?.inputs).serviceCompletionConfirmed;
  }
  return true;
}

/**
 * @param {unknown} raw
 * @returns {FormatFlags}
 */
function normalizeFormatFlags(raw) {
  if (!raw || typeof raw !== "object") {
    return createEmptyFormatFlags();
  }
  const record = /** @type {Record<string, unknown>} */ (raw);
  return {
    pdf: Boolean(record.pdf),
    image: Boolean(record.image),
    text: Boolean(record.text),
  };
}

/**
 * @param {unknown} raw
 * @returns {MilestoneAttachment}
 */
function normalizeAttachment(raw) {
  if (!raw || typeof raw !== "object") {
    return { assetRef: "" };
  }
  const record = /** @type {Record<string, unknown>} */ (raw);
  return {
    assetRef: String(record.assetRef ?? record.asset_ref ?? ""),
    fileName:
      record.fileName != null
        ? String(record.fileName)
        : record.file_name != null
          ? String(record.file_name)
          : undefined,
    mimeType:
      record.mimeType != null
        ? String(record.mimeType)
        : record.mime_type != null
          ? String(record.mime_type)
          : undefined,
  };
}

/**
 * @param {unknown} raw
 * @returns {EvidenceBlock}
 */
export function normalizeEvidenceBlock(raw) {
  if (!raw || typeof raw !== "object") {
    return createEmptyEvidenceBlock();
  }
  const record = /** @type {Record<string, unknown>} */ (raw);
  const formatRaw = record.formatFlags ?? record.format_flags;
  const attachmentsRaw = record.attachments;
  const sensitivity = record.sensitivity === "ip" ? "ip" : "confidential";

  return {
    formatFlags: normalizeFormatFlags(formatRaw),
    sensitivity,
    attachments: Array.isArray(attachmentsRaw)
      ? attachmentsRaw.map(normalizeAttachment).filter((a) => a.assetRef)
      : [],
    textNotes: String(record.textNotes ?? record.text_notes ?? ""),
  };
}

/**
 * @param {unknown} raw
 * @returns {GearUnitFinding}
 */
export function normalizeFinding(raw) {
  if (!raw || typeof raw !== "object") {
    return createEmptyFinding();
  }
  const record = /** @type {Record<string, unknown>} */ (raw);
  return {
    id: String(record.id ?? createFindingId()),
    gearUnitSerialNumber: String(
      record.gearUnitSerialNumber ?? record.gear_unit_serial_number ?? ""
    ),
    dimensionalMeasurements: normalizeEvidenceBlock(
      record.dimensionalMeasurements ?? record.dimensional_measurements
    ),
    ndtReports: normalizeEvidenceBlock(record.ndtReports ?? record.ndt_reports),
    visualInspectionImages: normalizeEvidenceBlock(
      record.visualInspectionImages ?? record.visual_inspection_images
    ),
  };
}

/**
 * @param {unknown} raw
 * @returns {ServiceComponent}
 */
export function normalizeServiceComponent(raw) {
  if (!raw || typeof raw !== "object") {
    return createEmptyServiceComponent();
  }
  const record = /** @type {Record<string, unknown>} */ (raw);
  return {
    id: String(record.id ?? createFindingId()),
    serialNumber: String(
      record.serialNumber ??
        record.serial_number ??
        record.gearUnitSerialNumber ??
        record.gear_unit_serial_number ??
        ""
    ),
  };
}

/**
 * @param {unknown[]} findings
 * @returns {ServiceComponent[]}
 */
function componentsFromLegacyFindings(findings) {
  return findings
    .map(normalizeFinding)
    .map((finding) => createEmptyServiceComponent(finding.gearUnitSerialNumber));
}

/**
 * @param {unknown[]} findings
 * @param {keyof GearUnitFinding} key
 * @returns {EvidenceBlock}
 */
function evidenceFromLegacyFindings(findings, key) {
  const normalized = findings.map(normalizeFinding);
  return (
    normalized.find((finding) => evidenceBlockHasContent(finding[key]))?.[key] ??
    normalized[0]?.[key] ??
    createEmptyEvidenceBlock()
  );
}

/**
 * @param {unknown} raw
 * @returns {M1Inputs}
 */
export function normalizeM1Inputs(raw) {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return createEmptyM1Inputs();
  }
  const record = /** @type {Record<string, unknown>} */ (raw);
  const findings = Array.isArray(record.findings) ? record.findings : [];
  const componentsRaw = record.components ?? record.serviceComponents;
  const components = Array.isArray(componentsRaw)
    ? componentsRaw.map(normalizeServiceComponent)
    : componentsFromLegacyFindings(findings);

  return {
    components,
    dimensionalMeasurements: normalizeEvidenceBlock(
      record.dimensionalMeasurements ??
        record.dimensional_measurements ??
        evidenceFromLegacyFindings(findings, "dimensionalMeasurements")
    ),
    ndtReports: normalizeEvidenceBlock(
      record.ndtReports ??
        record.ndt_reports ??
        evidenceFromLegacyFindings(findings, "ndtReports")
    ),
    visualInspectionImages: normalizeEvidenceBlock(
      record.visualInspectionImages ??
        record.visual_inspection_images ??
        evidenceFromLegacyFindings(findings, "visualInspectionImages")
    ),
  };
}

/**
 * @param {unknown} raw
 * @returns {M2Inputs}
 */
export function normalizeM2Inputs(raw) {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return createEmptyM2Inputs();
  }
  const record = /** @type {Record<string, unknown>} */ (raw);
  return {
    postRepairMeasurements: normalizeEvidenceBlock(
      record.postRepairMeasurements ?? record.post_repair_measurements
    ),
    heatTreatmentReferences: normalizeEvidenceBlock(
      record.heatTreatmentReferences ?? record.heat_treatment_references
    ),
    processConfirmation: normalizeEvidenceBlock(
      record.processConfirmation ?? record.process_confirmation
    ),
  };
}

/**
 * @param {unknown} raw
 * @returns {M3Inputs}
 */
export function normalizeM3Inputs(raw) {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return createEmptyM3Inputs();
  }
  const record = /** @type {Record<string, unknown>} */ (raw);
  return {
    finalInspectionReport: normalizeEvidenceBlock(
      record.finalInspectionReport ?? record.final_inspection_report
    ),
    serviceCompletionConfirmed: Boolean(
      record.serviceCompletionConfirmed ?? record.service_completion_confirmed
    ),
  };
}

/**
 * @param {unknown} milestone
 * @returns {boolean}
 */
export function isLegacyM1Milestone(milestone) {
  if (!milestone || typeof milestone !== "object") return false;
  const record = /** @type {Record<string, unknown>} */ (milestone);
  const id = String(record.id ?? "");
  const title = String(record.title ?? "");
  return id === "m-a" || title === "Assessment";
}

/**
 * @param {unknown} raw
 * @returns {ServiceMilestone}
 */
export function normalizeMilestone(raw) {
  if (!raw || typeof raw !== "object") {
    return { ...REMANUFACTURING_DEFAULT_MILESTONES[0] };
  }
  const record = /** @type {Record<string, unknown>} */ (raw);

  if (isLegacyM1Milestone(record)) {
    return normalizeMilestone({
      ...REMANUFACTURING_DEFAULT_MILESTONES[0],
      status: record.status ?? MILESTONE_STATUS.PENDING,
      inputs: record.inputs ?? createEmptyM1Inputs(),
    });
  }

  const legacyKeyById = {
    "m-a": MILESTONE_KEY.M1,
    "m-b": MILESTONE_KEY.M2,
    "m-c": MILESTONE_KEY.M3,
  };
  const id = String(record.id ?? "");
  const key =
    String(record.key ?? "") ||
    legacyKeyById[id] ||
    (isLegacyM1Milestone(record) ? MILESTONE_KEY.M1 : "");
  const defaultForKey = REMANUFACTURING_DEFAULT_MILESTONES.find((m) => m.key === key);
  const resolvedId = id || defaultForKey?.id || "";
  const rawTitle = String(record.title ?? defaultForKey?.title ?? "");
  const title =
    (key === MILESTONE_KEY.M2 && rawTitle === "Remanufacturing") ||
    (key === MILESTONE_KEY.M3 && rawTitle === "Delivery")
      ? String(defaultForKey?.title ?? rawTitle)
      : rawTitle;
  const status = /** @type {MilestoneStatus} */ (
    record.status === MILESTONE_STATUS.IN_PROGRESS ||
    record.status === MILESTONE_STATUS.COMPLETED
      ? record.status
      : MILESTONE_STATUS.PENDING
  );
  const sortOrder =
    typeof record.sortOrder === "number"
      ? record.sortOrder
      : typeof record.sort_order === "number"
        ? record.sort_order
        : (defaultForKey?.sortOrder ?? 1);

  /** @type {ServiceMilestone} */
  const milestone = {
    id: resolvedId,
    key: key || defaultForKey?.key || "",
    title,
    status,
    sortOrder,
  };

  if (milestone.key === MILESTONE_KEY.M1) {
    milestone.inputs = normalizeM1Inputs(record.inputs);
  }

  if (milestone.key === MILESTONE_KEY.M2) {
    milestone.inputs = normalizeM2Inputs(record.inputs);
  }

  if (milestone.key === MILESTONE_KEY.M3) {
    milestone.inputs = normalizeM3Inputs(record.inputs);
  }

  if (isFollowUpMilestoneKey(milestone.key)) {
    milestone.inputs = normalizeFollowUpInputs(record.inputs);
    if (!milestone.id) {
      const number = milestoneNumberFromKey(milestone.key);
      milestone.id = number != null ? `m${number}` : milestone.id;
    }
    if (!milestone.sortOrder && milestoneNumberFromKey(milestone.key) != null) {
      milestone.sortOrder = milestoneNumberFromKey(milestone.key);
    }
  }

  return milestone;
}

/**
 * @param {unknown[]} milestones
 * @returns {ServiceMilestone[]}
 */
export function normalizeRemanufacturingMilestonesList(milestones) {
  if (!Array.isArray(milestones) || milestones.length === 0) {
    return REMANUFACTURING_DEFAULT_MILESTONES.map(normalizeMilestone);
  }

  const normalized = milestones.map(normalizeMilestone);
  const hasM1 = normalized.some((m) => m.key === MILESTONE_KEY.M1);
  if (!hasM1) {
    const legacyIndex = milestones.findIndex(isLegacyM1Milestone);
    if (legacyIndex >= 0) {
      normalized[legacyIndex] = normalizeMilestone(milestones[legacyIndex]);
    }
  }

  for (const defaultMilestone of REMANUFACTURING_DEFAULT_MILESTONES) {
    if (!normalized.some((milestone) => milestone.key === defaultMilestone.key)) {
      normalized.push(normalizeMilestone(defaultMilestone));
    }
  }

  return syncFollowUpMilestones(normalized);
}

/**
 * @param {ServiceMilestone[]} milestones
 * @returns {ServiceMilestone|undefined}
 */
export function getActiveMilestone(milestones) {
  return milestones.find((m) => m.status !== MILESTONE_STATUS.COMPLETED);
}

/**
 * @param {ServiceMilestone|undefined|null} milestone
 * @returns {boolean}
 */
export function isM1(milestone) {
  return milestone?.key === MILESTONE_KEY.M1;
}

/**
 * @param {ServiceMilestone[]} milestones
 * @param {string} key
 * @param {Partial<ServiceMilestone>} patch
 * @returns {ServiceMilestone[]}
 */
export function updateMilestoneInList(milestones, key, patch) {
  return milestones.map((milestone) =>
    milestone.key === key ? { ...milestone, ...patch } : milestone
  );
}

/**
 * @param {EvidenceBlock|undefined} block
 * @returns {boolean}
 */
export function evidenceBlockHasContent(block) {
  if (!block) return false;
  if (String(block.textNotes ?? "").trim()) return true;
  return Array.isArray(block.attachments) && block.attachments.length > 0;
}

/**
 * @param {GearUnitFinding} finding
 * @returns {boolean}
 */
export function findingHasEvidence(finding) {
  return EVIDENCE_SECTION_KEYS.some((key) =>
    evidenceBlockHasContent(finding[key])
  );
}

/** @typedef {string} MilestoneValidationMessageKey */

/**
 * @typedef {Object} MilestoneCompletionErrors
 * @property {Record<string, MilestoneValidationMessageKey>} [fields]
 * @property {Record<string, MilestoneValidationMessageKey>} [components]
 * @property {Record<string, MilestoneValidationMessageKey>} [sections]
 */

/**
 * @param {MilestoneCompletionErrors} errors
 * @returns {MilestoneValidationMessageKey}
 */
export function firstMilestoneValidationMessageKey(errors) {
  const fieldIssue = Object.values(errors.fields ?? {})[0];
  if (fieldIssue) return fieldIssue;
  const sectionIssue = Object.values(errors.sections ?? {})[0];
  if (sectionIssue) return sectionIssue;
  const componentIssue = Object.values(errors.components ?? {})[0];
  if (componentIssue) return componentIssue;
  return "ordersReceivedMilestoneError";
}

/** Backward-compatible alias used by older callers/tests. */
export const firstM1ValidationMessageKey = firstMilestoneValidationMessageKey;

/**
 * @param {MilestoneCompletionErrors} errors
 * @returns {{ ok: true } | { ok: false, errors: MilestoneCompletionErrors }}
 */
function validationResult(errors) {
  const hasFieldErrors = Object.keys(errors.fields ?? {}).length > 0;
  const hasComponentErrors = Object.keys(errors.components ?? {}).length > 0;
  const hasSectionErrors = Object.keys(errors.sections ?? {}).length > 0;
  return hasFieldErrors || hasComponentErrors || hasSectionErrors
    ? { ok: false, errors }
    : { ok: true };
}

/**
 * Field-level validation when completing M1 (client-side; mirrors backend).
 * Save progress does not run these checks.
 *
 * @param {ServiceMilestone|undefined|null} milestone
 * @returns {{ ok: true } | { ok: false, errors: MilestoneCompletionErrors }}
 */
export function validateM1Completion(milestone) {
  const inputs = normalizeM1Inputs(milestone?.inputs);
  /** @type {MilestoneCompletionErrors} */
  const errors = { fields: {}, components: {} };

  if (inputs.components.length === 0) {
    errors.fields.components = "m1ValidationNoComponents";
  }

  for (const component of inputs.components) {
    if (!String(component.serialNumber ?? "").trim()) {
      errors.components[component.id] = "m1ValidationComponentRequired";
    }
  }

  if (!EVIDENCE_SECTION_KEYS.some((key) => evidenceBlockHasContent(inputs[key]))) {
    errors.fields.evidence = "m1ValidationEvidenceRequired";
  }

  return validationResult(errors);
}

/**
 * @param {ServiceMilestone|undefined|null} milestone
 * @returns {{ ok: true } | { ok: false, errors: MilestoneCompletionErrors }}
 */
export function validateM2Completion(milestone) {
  const inputs = normalizeM2Inputs(milestone?.inputs);
  /** @type {MilestoneCompletionErrors} */
  const errors = { fields: {} };
  const required = {
    postRepairMeasurements: "m2ValidationPostRepairMeasurementsRequired",
    heatTreatmentReferences: "m2ValidationHeatTreatmentReferencesRequired",
    processConfirmation: "m2ValidationProcessConfirmationRequired",
  };

  for (const key of M2_EVIDENCE_SECTION_KEYS) {
    if (!evidenceBlockHasContent(inputs[key])) {
      errors.fields[key] = required[key];
    }
  }

  return validationResult(errors);
}

/**
 * @param {ServiceMilestone|undefined|null} milestone
 * @returns {{ ok: true } | { ok: false, errors: MilestoneCompletionErrors }}
 */
export function validateM3Completion(milestone) {
  const inputs = normalizeM3Inputs(milestone?.inputs);
  /** @type {MilestoneCompletionErrors} */
  const errors = { fields: {} };

  if (!evidenceBlockHasContent(inputs.finalInspectionReport)) {
    errors.fields.finalInspectionReport =
      "m3ValidationFinalInspectionReportRequired";
  }

  return validationResult(errors);
}

/**
 * @param {ServiceMilestone|undefined|null} milestone
 * @returns {{ ok: true } | { ok: false, errors: MilestoneCompletionErrors }}
 */
export function validateFollowUpCompletion(milestone) {
  const inputs = normalizeFollowUpInputs(milestone?.inputs);
  /** @type {MilestoneCompletionErrors} */
  const errors = { fields: {}, sections: {} };

  if (!String(milestone?.title ?? "").trim()) {
    errors.fields.title = "followUpValidationTitleRequired";
  }

  if (!hasCompleteCustomEvidenceSection(inputs.customSections)) {
    errors.fields.sections = "followUpValidationSectionRequired";
  }

  for (const section of inputs.customSections) {
    const label = String(section.label ?? "").trim();
    const labelConfirmed = Boolean(section.labelConfirmed);
    if (labelConfirmed && label && !evidenceBlockHasContent(section.evidence)) {
      errors.sections[section.id] = "followUpValidationSectionEvidenceRequired";
    }
    if (
      !labelConfirmed &&
      (evidenceBlockHasContent(section.evidence) || label)
    ) {
      errors.sections[section.id] = "followUpValidationSectionLabelRequired";
    }
  }

  return validationResult(errors);
}

/** @deprecated Use validateFollowUpCompletion */
export const validateM4Completion = validateFollowUpCompletion;

/**
 * @param {string} key
 * @param {number} [sortOrder]
 * @returns {ServiceMilestone}
 */
export function createFollowUpMilestone(key = MILESTONE_KEY.M4, sortOrder) {
  const number = milestoneNumberFromKey(key) ?? 4;
  return {
    id: `m${number}`,
    key,
    title: "",
    status: MILESTONE_STATUS.PENDING,
    sortOrder: sortOrder ?? number,
    inputs: createEmptyFollowUpInputs(),
  };
}

/**
 * @param {ServiceMilestone[]} milestones
 * @returns {ServiceMilestone[]}
 */
function sortMilestonesByOrder(milestones) {
  return [...milestones].sort(
    (a, b) =>
      (a.sortOrder ?? Number.MAX_SAFE_INTEGER) -
      (b.sortOrder ?? Number.MAX_SAFE_INTEGER)
  );
}

/**
 * @param {ServiceMilestone[]} milestones
 * @returns {ServiceMilestone[]}
 */
function getFollowUpChain(milestones) {
  return milestones
    .filter(
      (milestone) =>
        milestone.key === MILESTONE_KEY.M3 || isFollowUpMilestoneKey(milestone.key)
    )
    .sort(
      (a, b) =>
        (a.sortOrder ?? Number.MAX_SAFE_INTEGER) -
        (b.sortOrder ?? Number.MAX_SAFE_INTEGER)
    );
}

/**
 * Drops M4+ milestones after the first milestone where service completion is confirmed.
 * @param {ServiceMilestone[]} milestones
 * @returns {ServiceMilestone[]}
 */
export function pruneFollowUpMilestonesAfterTerminal(milestones) {
  const terminal = getFollowUpChain(milestones).find((milestone) =>
    getServiceCompletionConfirmed(milestone)
  );
  if (!terminal) {
    return milestones;
  }

  const terminalNumber = milestoneNumberFromKey(terminal.key);
  if (terminalNumber == null) {
    return milestones;
  }

  return milestones.filter((milestone) => {
    const number = milestoneNumberFromKey(milestone.key);
    if (number == null || number < 4) {
      return true;
    }
    return number <= terminalNumber;
  });
}

/**
 * Removes extra pending follow-up stubs left from earlier buggy sync (keeps one preview stub).
 * @param {ServiceMilestone[]} milestones
 * @returns {ServiceMilestone[]}
 */
function pruneRedundantPendingFollowUpStubs(milestones) {
  const chain = getFollowUpChain(milestones);
  if (chain.length === 0) {
    return milestones;
  }

  let highestSubstantiveNumber = milestoneNumberFromKey(MILESTONE_KEY.M3) ?? 3;
  for (const milestone of chain) {
    const number = milestoneNumberFromKey(milestone.key);
    if (number == null) {
      continue;
    }
    if (
      milestone.status === MILESTONE_STATUS.COMPLETED ||
      milestone.status === MILESTONE_STATUS.IN_PROGRESS
    ) {
      highestSubstantiveNumber = Math.max(highestSubstantiveNumber, number);
    }
  }

  const terminal = chain.find((milestone) => getServiceCompletionConfirmed(milestone));
  if (terminal) {
    return milestones;
  }

  const allowedMaxNumber = highestSubstantiveNumber + 1;
  const pendingBeyondAllowed = chain.filter((milestone) => {
    const number = milestoneNumberFromKey(milestone.key);
    return (
      number != null &&
      number > allowedMaxNumber &&
      milestone.status === MILESTONE_STATUS.PENDING
    );
  });

  if (pendingBeyondAllowed.length === 0) {
    return milestones;
  }

  return milestones.filter((milestone) => {
    const number = milestoneNumberFromKey(milestone.key);
    if (number == null || number < 4) {
      return true;
    }
    if (milestone.status !== MILESTONE_STATUS.PENDING) {
      return true;
    }
    return number <= allowedMaxNumber;
  });
}

/**
 * Keeps follow-up milestones (M4+) in sync with service-completion checkboxes.
 * @param {ServiceMilestone[]} milestones
 * @returns {ServiceMilestone[]}
 */
export function syncFollowUpMilestones(milestones) {
  let result = pruneRedundantPendingFollowUpStubs(
    pruneFollowUpMilestonesAfterTerminal(milestones)
  );
  const chain = getFollowUpChain(result);
  const last = chain[chain.length - 1];

  if (!last || getServiceCompletionConfirmed(last)) {
    return sortMilestonesByOrder(result);
  }

  const nextKey = nextFollowUpMilestoneKey(last.key);
  if (!result.some((milestone) => milestone.key === nextKey)) {
    const nextNumber = milestoneNumberFromKey(nextKey);
    result = [
      ...result,
      createFollowUpMilestone(nextKey, nextNumber ?? chain.length + 1),
    ];
  }

  return sortMilestonesByOrder(result);
}

/** @deprecated Use syncFollowUpMilestones */
export const syncFollowUpMilestoneWithM3 = syncFollowUpMilestones;

/**
 * @param {string} key
 * @param {(key: string) => string} translate
 * @returns {string}
 */
export function defaultTitleForMilestoneKey(key, translate) {
  const titleKeyByMilestone = {
    [MILESTONE_KEY.M1]: "m1Title",
    [MILESTONE_KEY.M2]: "m2Title",
    [MILESTONE_KEY.M3]: "m3Title",
  };
  const translationKey = titleKeyByMilestone[key];
  if (translationKey) {
    return translate(translationKey);
  }
  if (isFollowUpMilestoneKey(key)) {
    const number = milestoneNumberFromKey(key);
    return translate("followUpDefaultTitle").replace(
      "{number}",
      String(number ?? "")
    );
  }
  return "";
}

/**
 * @param {ServiceMilestone|undefined|null} milestone
 * @param {(key: string) => string} translate
 * @returns {string}
 */
export function resolveMilestoneDisplayTitle(milestone, translate) {
  const custom = String(milestone?.title ?? "").trim();
  if (custom) return custom;
  return defaultTitleForMilestoneKey(milestone?.key ?? "", translate);
}

/**
 * @param {ServiceMilestone|undefined|null} milestone
 * @returns {{ ok: boolean, reason?: MilestoneValidationMessageKey, errors?: MilestoneCompletionErrors }}
 */
export function canCompleteMilestone(milestone) {
  const validation =
    milestone?.key === MILESTONE_KEY.M1
      ? validateM1Completion(milestone)
      : milestone?.key === MILESTONE_KEY.M2
        ? validateM2Completion(milestone)
        : milestone?.key === MILESTONE_KEY.M3
          ? validateM3Completion(milestone)
          : isFollowUpMilestoneKey(milestone?.key)
            ? validateFollowUpCompletion(milestone)
            : { ok: true };

  if (validation.ok) {
    return { ok: true };
  }
  return {
    ok: false,
    reason: firstMilestoneValidationMessageKey(validation.errors),
    errors: validation.errors,
  };
}

/**
 * @param {ServiceMilestone|undefined|null} milestone
 * @returns {{ ok: boolean, reason?: MilestoneValidationMessageKey, errors?: MilestoneCompletionErrors }}
 */
export function canCompleteM1(milestone) {
  return canCompleteMilestone(milestone);
}

/**
 * @param {ServiceMilestone[]} milestones
 * @returns {number}
 */
export function countM1DocumentedGearUnits(milestones) {
  const m1 = milestones.find((m) => m.key === MILESTONE_KEY.M1);
  const inputs = normalizeM1Inputs(m1?.inputs);
  return inputs.components.filter((component) =>
    String(component.serialNumber ?? "").trim()
  ).length;
}

/**
 * @param {ServiceMilestone|undefined|null} milestone
 * @returns {number}
 */
export function countCompletedEvidenceSections(milestone) {
  if (!milestone?.inputs) return 0;
  if (isFollowUpMilestoneKey(milestone.key)) {
    const inputs = normalizeFollowUpInputs(milestone.inputs);
    return inputs.customSections.filter((section) =>
      evidenceBlockHasContent(section.evidence)
    ).length;
  }
  const keys =
    milestone.key === MILESTONE_KEY.M1
      ? EVIDENCE_SECTION_KEYS
      : milestone.key === MILESTONE_KEY.M2
        ? M2_EVIDENCE_SECTION_KEYS
        : milestone.key === MILESTONE_KEY.M3
          ? M3_EVIDENCE_SECTION_KEYS
          : [];
  return keys.filter((key) => evidenceBlockHasContent(milestone.inputs[key]))
    .length;
}

/**
 * @param {string} mimeType
 * @returns {Partial<FormatFlags>}
 */
export function formatFlagsFromMimeType(mimeType) {
  const mime = String(mimeType ?? "").toLowerCase();
  if (mime.startsWith("image/")) {
    return { image: true };
  }
  if (mime === "application/pdf") {
    return { pdf: true };
  }
  if (mime.startsWith("text/")) {
    return { text: true };
  }
  return {};
}

/**
 * @param {FormatFlags} flags
 * @param {Partial<FormatFlags>} patch
 * @returns {FormatFlags}
 */
export function mergeFormatFlags(flags, patch) {
  return {
    pdf: Boolean(flags.pdf || patch.pdf),
    image: Boolean(flags.image || patch.image),
    text: Boolean(flags.text || patch.text),
  };
}
